import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, SwipeableDrawer, Typography } from '@mui/material';
import React, { ReactElement } from 'react';
import { DeleteIcon } from 'components/icons';
import type { OrganizationMember } from 'services/api';
import { formatPeopleDate } from '../formatPeopleDate';

const TEXT_COLOR = '#383838';
const DIVIDER_COLOR = '#DEDEDE';
const COLOR_REMOVE = '#9B0000';

function Label ({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <Typography
      sx={{
        fontFamily: '\'Poppins\', sans-serif',
        fontSize: '10px',
        fontWeight: 600,
        lineHeight: '18px',
        textTransform: 'uppercase',
        color: TEXT_COLOR,
      }}
    >
      {children}
    </Typography>
  );
}

function Value ({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <Typography
      sx={{
        fontFamily: '\'Poppins\', sans-serif',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '21px',
        color: TEXT_COLOR,
      }}
    >
      {children}
    </Typography>
  );
}

export function MemberDetailBottomSheet ({
  open,
  member,
  canRemove,
  onClose,
  onRemove,
}: {
  open: boolean;
  member: OrganizationMember | null;
  canRemove: boolean;
  onClose: () => void;
  onRemove: (member: OrganizationMember) => void;
}): ReactElement {
  if (!member) return null as unknown as ReactElement;

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => undefined}
      disableSwipeToOpen
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: 'flex-start',
          pt: '14px',
          px: '20px',
          pb: '36px',
          backgroundColor: 'white',
        }}
      >
        <Box display="flex" justifyContent="flex-end" width="100%">
          <IconButton onClick={onClose} size="small" sx={{ p: 0 }}>
            <CloseIcon sx={{ fontSize: 19, color: TEXT_COLOR }} />
          </IconButton>
        </Box>

        <Box display="flex" flexDirection="column" gap="3px" width="100%">
          <Typography
            sx={{
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '24px',
              color: TEXT_COLOR,
            }}
          >
            {member.firstName} {member.lastName}
          </Typography>
          <Typography
            sx={{
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '18px',
              color: TEXT_COLOR,
            }}
          >
            {member.schoolEmail}
          </Typography>
        </Box>

        <Box sx={{ width: '100%', height: '1px', backgroundColor: DIVIDER_COLOR }} />

        <Box display="flex" gap="16px" width="100%">
          <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap="4px">
            <Label>CLASSIFICATION</Label>
            <Value>{member.classification || '—'}</Value>
          </Box>
          <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap="4px">
            <Label>MAJOR</Label>
            <Value>{member.major || '—'}</Value>
          </Box>
        </Box>

        <Box display="flex" gap="16px" width="100%">
          <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap="4px">
            <Label>DATE JOINED</Label>
            <Value>{formatPeopleDate(member.dateJoined)}</Value>
          </Box>
          <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap="4px">
            <Label>LAST ATTENDED</Label>
            <Value>{formatPeopleDate(member.lastAttended)}</Value>
          </Box>
        </Box>

        {canRemove ? (
          <Box
            component="button"
            type="button"
            onClick={() => onRemove(member)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0,
              border: `1px solid ${COLOR_REMOVE}`,
              borderRadius: '10px',
              backgroundColor: '#FFFFFF',
              padding: '10px',
              height: 43,
              width: '100%',
              cursor: 'pointer',
              color: COLOR_REMOVE,
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              '&:hover': { backgroundColor: '#FAFAFA' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'inherit', '& svg': { width: 23, height: 23 } }}>
              <DeleteIcon />
            </Box>
            <Typography component="span" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}>
              Remove
            </Typography>
          </Box>
        ) : null}

        <Box
          component="button"
          type="button"
          onClick={onClose}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            p: '15px',
          }}
        >
          <Typography
            sx={{
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '24px',
              color: TEXT_COLOR,
              textDecoration: 'underline',
            }}
          >
            Close
          </Typography>
        </Box>
      </Box>
    </SwipeableDrawer>
  );
}
